/**
 * API Configuration for Kushal's AI Attribute Manager
 * Save this file as: frontend/src/config.js
 */

// Get API base URL from environment variable
// Empty string = relative URLs (works with nginx proxy in production)
// For local dev without proxy, create frontend/.env.local with: REACT_APP_API_URL=http://localhost:5000
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// API key for authentication (optional - set via REACT_APP_API_KEY env var)
const API_KEY = process.env.REACT_APP_API_KEY || '';

/**
 * Default headers for all API calls (includes API key if configured)
 */
const defaultHeaders = {
  'Content-Type': 'application/json',
  ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
};

/**
 * Wrapper around fetch that automatically includes auth headers
 */
const apiFetch = (url, options = {}) => {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...defaultHeaders,
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers,
  };
  return fetch(url, { ...options, headers }).then(response => {
    if (response.status === 401 && !url.includes('/api/auth/')) {
      // Session expired/invalid: clear credentials and let the app redirect to
      // the login screen via React state. A custom event (instead of a hard
      // window.location.reload) avoids the blank-page flash and preserves SPA state.
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }
    return response;
  });
};

/**
 * API Configuration Object
 */
const config = {
  // Base URL for all API calls
  apiBaseUrl: API_BASE_URL,

  // Default headers (includes API key)
  headers: defaultHeaders,

  // Authenticated fetch wrapper
  apiFetch,

  // API Endpoints - All the endpoints your app needs
  endpoints: {
    // Design endpoints
    designs: `${API_BASE_URL}/api/designs`,
    designsGenerate: `${API_BASE_URL}/api/generate`,
    designsStatus: (jobId) => `${API_BASE_URL}/api/status/${jobId}`,
    designsRerun: (designNumber) => `${API_BASE_URL}/api/rerun/${designNumber}`,
    designByNumber: (designNumber) => `${API_BASE_URL}/api/designs/${designNumber}`,
    designVariants: (designNumber) => `${API_BASE_URL}/api/designs/${designNumber}/variants`,

    // Attribute endpoints
    attributesConfig: `${API_BASE_URL}/api/attributes/config`,
    coreAttributes: `${API_BASE_URL}/api/attributes/core`,
    catalogUpload: `${API_BASE_URL}/api/catalog/upload`,
    catalogUpdate: `${API_BASE_URL}/api/catalog/update`,
    catalogUpdateTemplate: `${API_BASE_URL}/api/catalog/update/template.csv`,
    attributesToggle: (name) => `${API_BASE_URL}/api/attributes/toggle/${name}`,
    attributesAddValue: (name) => `${API_BASE_URL}/api/attributes/${name}/add-value`,
    attributesRemoveValue: (name, value) => `${API_BASE_URL}/api/attributes/${name}/values/${value}`,
    attributesValues: `${API_BASE_URL}/api/attributes/values`,
    attributes: `${API_BASE_URL}/api/attributes`,
    attributeById: (id) => `${API_BASE_URL}/api/attributes/${id}`,

    // Category endpoints
    categories: `${API_BASE_URL}/api/categories`,
    categoryById: (id) => `${API_BASE_URL}/api/categories/${id}`,
    categoriesReorder: `${API_BASE_URL}/api/categories/reorder`,
    categoryAttributes: (id) => `${API_BASE_URL}/api/categories/${id}/attributes`,

    // Collection endpoints
    collections: `${API_BASE_URL}/api/collections`,
    collectionsAttributesValues: `${API_BASE_URL}/api/collections/attributes/values`,
    collectionById: (id) => `${API_BASE_URL}/api/collections/${id}`,
    collectionDesigns: (id) => `${API_BASE_URL}/api/collections/${id}/designs`,
    collectionAddDesign: (collectionId, designNumber) =>
      `${API_BASE_URL}/api/collections/${collectionId}/designs/${designNumber}`,
    collectionMetrics: (id) => `${API_BASE_URL}/api/collections/${id}/metrics`,
    collectionTrending: (id) => `${API_BASE_URL}/api/collections/${id}/trending`,

    // Trending endpoints
    trendingAttributes: `${API_BASE_URL}/api/trending/attributes`,
    trendingDesigns: `${API_BASE_URL}/api/trending/designs`,
    trendingHiddenGems: `${API_BASE_URL}/api/trending/hidden-gems`,
    trendingRecalculate: `${API_BASE_URL}/api/trending/recalculate`,
    trendingPredict: `${API_BASE_URL}/api/trending/predict`,

    // Channel platform endpoints
    channelPlatforms: `${API_BASE_URL}/api/channels/platforms`,
    channelPlatform: (id) => `${API_BASE_URL}/api/channels/platforms/${id}`,
    channelConfig: (id) => `${API_BASE_URL}/api/channels/${id}/config`,
    channelDesigns: (id) => `${API_BASE_URL}/api/channels/${id}/designs`,
    channelDesignDetail: (id, dn) => `${API_BASE_URL}/api/channels/${id}/designs/${dn}`,
    channelPush: `${API_BASE_URL}/api/channels/push`,
    channelDesignPlatforms: (dn) => `${API_BASE_URL}/api/channels/designs/${dn}/platforms`,
    channelExport: (id, template) => template
      ? `${API_BASE_URL}/api/channels/${id}/export?template=${template}`
      : `${API_BASE_URL}/api/channels/${id}/export`,
    channelExportTemplates: (id) => `${API_BASE_URL}/api/channels/${id}/export/templates`,
    channelBackfill: `${API_BASE_URL}/api/channels/backfill`,
    channelBackfillVariants: `${API_BASE_URL}/api/channels/backfill-variants`,
    channelCredentials: (id) => `${API_BASE_URL}/api/channels/${id}/credentials`,
    channelTestConnection: (id) => `${API_BASE_URL}/api/channels/${id}/test-connection`,
    channelPublishDesign: (id, dn) => `${API_BASE_URL}/api/channels/${id}/publish/${dn}`,
    channelPublishBulk: (id) => `${API_BASE_URL}/api/channels/${id}/publish-bulk`,
    channelDesignImages: (id, dn) => `${API_BASE_URL}/api/channels/${id}/designs/${dn}/images`,

    // Channel collection endpoints
    channelCollections: (platformId) => `${API_BASE_URL}/api/channels/${platformId}/collections`,
    channelCollection: (collectionId) => `${API_BASE_URL}/api/channels/collections/${collectionId}`,
    channelCollectionDesigns: (collectionId) => `${API_BASE_URL}/api/channels/collections/${collectionId}/designs`,
    channelCollectionPublish: (collectionId) => `${API_BASE_URL}/api/channels/collections/${collectionId}/publish`,
    channelCollectionDesignRemove: (collectionId, dn) => `${API_BASE_URL}/api/channels/collections/${collectionId}/designs/${dn}`,

    // Design images
    designImages: (designNumber) => `${API_BASE_URL}/api/designs/${designNumber}/images`,

    // Auth endpoints
    authLogin: `${API_BASE_URL}/api/auth/login`,
    authMe: `${API_BASE_URL}/api/auth/me`,
    authChangePassword: `${API_BASE_URL}/api/auth/change-password`,

    // User management endpoints
    users: `${API_BASE_URL}/api/users`,
    userById: (id) => `${API_BASE_URL}/api/users/${id}`,

    // API key management endpoints
    apiKeys: `${API_BASE_URL}/api/admin/api-keys`,
    apiKeyById: (id) => `${API_BASE_URL}/api/admin/api-keys/${id}`,

    // Activity log endpoints
    activity: `${API_BASE_URL}/api/activity`,
    activityStats: `${API_BASE_URL}/api/activity/stats`,
    activityForDesign: (dn) => `${API_BASE_URL}/api/activity/design/${dn}`,

    // Utility endpoints
    health: `${API_BASE_URL}/api/health`,
    promptPreview: `${API_BASE_URL}/api/prompt-preview`,

    // Catalog enrichment endpoints (uses JWT auth, not API key)
    enrichCombination: (cn) => `${API_BASE_URL}/api/enrichment/combinations/${cn}`,
    enrichmentCounts: `${API_BASE_URL}/api/enrichment/counts`,
    enrichmentFacets: `${API_BASE_URL}/api/enrichment/facets`,
    // External designs list (requires API key, or use externalDesignsUI for JWT auth)
    externalDesigns: `${API_BASE_URL}/api/v1/catalog/designs`,
    externalDesignsUI: `${API_BASE_URL}/api/enrichment/designs`,
  },

  // Helper method to build custom URLs
  buildUrl: (path) => {
    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${API_BASE_URL}${cleanPath}`;
  },

  // Log configuration (for debugging)
  logConfig: () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('API Configuration:');
      console.log(`   Environment: ${process.env.NODE_ENV}`);
      console.log(`   API Base URL: ${API_BASE_URL}`);
      console.log(`   API Key: ${API_KEY ? 'configured' : 'not set'}`);
      console.log(`   Is Local: ${API_BASE_URL.includes('localhost')}`);
    }
  },

  // Channel API helpers (apiFetch-based)
  channels: {
    // ── Shopify Product Import & Analytics ──────────────────────────────────────
    getChannelProducts: (platformId, { cursor, limit = 50, status = 'ACTIVE' } = {}) => {
      const params = new URLSearchParams();
      if (cursor) params.set('cursor', cursor);
      params.set('limit', limit);
      params.set('status', status);
      return apiFetch(`${API_BASE_URL}/api/channels/${platformId}/products?${params}`);
    },

    getProductAnalytics: (platformId, productIds, days = 30) =>
      apiFetch(`${API_BASE_URL}/api/channels/${platformId}/products/analytics`, {
        method: 'POST',
        body: JSON.stringify({ productIds, days }),
      }),

    getProductTrends: (platformId, productId, { period = 'daily', days = 30 } = {}) => {
      const encodedId = encodeURIComponent(productId);
      return apiFetch(`${API_BASE_URL}/api/channels/${platformId}/products/${encodedId}/trends?period=${period}&days=${days}`);
    },
  },
};

// Log configuration in development
if (process.env.NODE_ENV === 'development') {
  config.logConfig();
}

export default config;
